import React, { useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { 
  FaEnvelope, 
  FaPhoneAlt, 
  FaGithub, 
  FaLinkedin, 
  FaInstagram, 
  FaPaperPlane 
} from "react-icons/fa";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* Seção de Contato */
const ContactSection = styled.section`
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #2a5298, #1a3a6b);
  color: #fff;
  font-family: "Segoe UI", sans-serif;
  text-align: center;
  min-height: 100vh;
  box-sizing: border-box;
`;

/* Título */
const ContactTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: #ffa726;
  animation: ${fadeIn} 0.6s ease-out;
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

/* Grid de contatos (ícones) */
const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  max-width: 800px;
  margin: 0 auto 2rem;
  box-sizing: border-box;
`;

/* Cartão de Contato */
const ContactCard = styled.a`
  background: #fff;
  color: #2a5298;
  padding: 1rem;
  border-radius: 10px;
  text-decoration: none;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  svg {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }
  
  h3 {
    margin: 0.5rem 0;
    font-size: 1.2rem;
  }
  
  p {
    font-size: 0.9rem;
    margin: 0;
  }
`;

/* Wrapper do formulário */
const FormWrapper = styled.div`
  background: #fff;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.6s ease-out;
  box-sizing: border-box;

  @media (max-width: 480px) {
    width: 90%;
    padding: 1rem;
  }
`;

/* Formulário */
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-sizing: border-box;
`;

/* Inputs e Textarea (full width) */
const Input = styled.input`
  padding: 0.8rem;
  border: 2px solid #e2e8f0;
  border-radius: 5px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    border-color: #2a5298;
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: 0.8rem;
  border: 2px solid #e2e8f0;
  border-radius: 5px;
  font-size: 1rem;
  resize: vertical;
  width: 100%;
  min-height: 120px;
  box-sizing: border-box;
  
  &:focus {
    border-color: #2a5298;
    outline: none;
  }
`;

/* Botão de envio */
const Button = styled.button`
  padding: 0.8rem;
  background: #ffa726;
  color: #fff;
  border: none;
  border-radius: 5px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    background: #1a3a6b;
  }
  
  &:disabled {
    background: #718096;
    cursor: not-allowed;
  }
`;

/* Mensagem de status com tipagem para a prop 'error' */
interface StatusMessageProps {
  error?: boolean;
}

const StatusMessage = styled.p<StatusMessageProps>`
  font-size: 1rem;
  color: ${({ error }) => (error ? "#e53e3e" : "#38a169")};
  text-align: center;
  box-sizing: border-box;
`;

/* Loader (animação) */
const Loader = styled.div`
  border: 3px solid rgba(255,255,255,0.3);
  border-top: 3px solid #fff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface FormData {
  name: string;
  email: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<{ type: "idle" | "success" | "error"; message: string }>({
    type: "idle",
    message: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );
  
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setIsLoading(true);
      setStatus({ type: "idle", message: "" });
      
      // Simulação de envio (substitua pela lógica real)
      await new Promise((resolve) => setTimeout(resolve, 2000));
      
      if (formData.email.includes("@")) {
        setStatus({ type: "success", message: "Mensagem enviada com sucesso!" });
        setFormData({ name: "", email: "", message: "" });
      } else {
        setStatus({ type: "error", message: "Erro: e-mail inválido." });
      }
      
      setIsLoading(false);
    },
    [formData]
  );
  
  return (
    <ContactSection id="contact">
      <ContactTitle>Entre em Contato</ContactTitle>
      
      <ContactGrid>
        <ContactCard href="mailto:mateus.martins@sou.unifal-mg.edu.br">
          <FaEnvelope />
          <h3>Email</h3>
          <p>mateus.martins@sou.unifal-mg.edu.br</p>
        </ContactCard>
        <ContactCard href="tel:35 99161-0936">
          <FaPhoneAlt />
          <h3>Telefone</h3>
          <p>35 99161-0936</p>
        </ContactCard>
        <ContactCard 
          href="https://github.com/mateus0205" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <FaGithub />
          <h3>GitHub</h3>
          <p>Mateus Henrique Martins</p>
        </ContactCard>
        <ContactCard 
          href="https://linkedin.com/in/mateus-henrique-martins" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <FaLinkedin />
          <h3>LinkedIn</h3>
          <p>Mateus Martins</p>
        </ContactCard>
        <ContactCard 
          href="https://instagram.com/mateus.martins02" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <FaInstagram />
          <h3>Instagram</h3>
          <p>@mateus.martins02</p>
        </ContactCard>
      </ContactGrid>
      
      <FormWrapper>
        <Form onSubmit={handleSubmit}>
          <Input 
            type="text" 
            name="name" 
            placeholder="Seu Nome" 
            value={formData.name} 
            onChange={handleChange} 
            required 
          />
          <Input 
            type="email" 
            name="email" 
            placeholder="Seu Email" 
            value={formData.email} 
            onChange={handleChange} 
            required 
          />
          <TextArea 
            name="message" 
            placeholder="Sua Mensagem" 
            value={formData.message} 
            onChange={handleChange} 
            required 
          />
          <Button type="submit" disabled={isLoading}>
            {isLoading ? <Loader /> : <>
              <FaPaperPlane /> Enviar Mensagem (em construção)
            </>}
          </Button>
          {status.type !== "idle" && (
            <StatusMessage error={status.type === "error"}>
              {status.message}
            </StatusMessage>
          )}
        </Form>
      </FormWrapper>
    </ContactSection>
  );
};

export default Contact;
